import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { AuthService, CompanyStoreService, RedirectService } from '@fleet-beat/shared';
import { CompanyDto, ConfirmDialogService, GetOneDto, PageableListV2Dto } from '@fleet-beat/core';
import { catchError, map, Observable, of, switchMap, take, tap } from 'rxjs';
import {
  CompaniesApiService,
  CompanySubscription,
  PricesApiService,
  TrucksApiService,
  VehicleDto,
} from '@fleet-beat/api';

@Injectable({ providedIn: 'root' })
export class CanActiveSubscriptionGuard implements CanActivate {
  constructor(
    private companyStore: CompanyStoreService,
    private redirectService: RedirectService,
    private confirmDialogService: ConfirmDialogService,
    private trucksApiService: TrucksApiService,
    private companiesApiService: CompaniesApiService,
    private authService: AuthService,
    private pricesApiService: PricesApiService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const companyId = this.companyStore.companyId;

    if (!companyId) {
      this.redirectService.navigate(['/']);
      return of(false);
    }

    const params: GetOneDto = { include: 'subscription,price' };

    return this.companiesApiService.getOne(companyId, this.authService.authHeaders, params).pipe(
      take(1),
      tap((company: CompanyDto) => {
        if (company) this.companyStore.setCompany(company);
      }),

      switchMap((company: CompanyDto) => {
        if (company?.price?.amount === 0) {
          return of(true);
        }
        const subscription: CompanySubscription | null | undefined = company?.subscription;

        if (!subscription) {
          this.redirectService.navigate(['/billing']);
          this.showSubscribeDialog();
          return of(false);
        }

        const limit: number = subscription.quantity ?? 0;

        return this.trucksApiService
          .getManyV2({ isActive: true, companyIds: this.companyStore.companyId }, this.authService.authHeaders)
          .pipe(
            take(1),
            map((res: PageableListV2Dto<VehicleDto>): boolean => {
              const activeCount: number = res?.total ?? 0;
              const isVehiclesPage: boolean = state.url.includes('/vehicles');
              const isVehicleDetailsPage: boolean = state.url.includes('/vehicle');

              if (activeCount > limit) {
                if (isVehiclesPage) {
                  this.showOverlimitDialog();
                  return true;
                }
                if (isVehicleDetailsPage) {
                  return true;
                }
                this.redirectService.navigate(['/billing']);
                this.showOverlimitDialog();
                return false;
              }

              return true;
            }),
          );
      }),
      catchError(() => {
        this.redirectService.navigate(['/billing']);
        this.showSubscribeDialog();
        return of(false);
      }),
    );
  }
  private showSubscribeDialog(): void {
    this.confirmDialogService.confirm({
      title: 'Subscribe now',
      text: `To access the logbook and vehicles, please subscribe on the billing page.`,
      buttons: [{ caption: 'Okay', color: 'success' }],
    });
  }

  private showOverlimitDialog(): void {
    this.confirmDialogService.confirm({
      title: 'Adjust your vehicle count',
      text: 'You have more active vehicles than your plan allows. Please deactivate extra vehicles or update your subscription to increase the limit.',
      buttons: [{ caption: 'Okay', color: 'success' }],
    });
  }
}
